import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Breadcrumbs } from '@/components/layout/Breadcrumbs';
import { PageContainer } from '@/components/layout/PageContainer';
import { BackButton } from '@/components/composite/buttons/BackButton';
import { RatingConfigTab } from '@/components/daily-reveal/RatingConfigTab';
import { CardsTab } from '@/components/daily-reveal/CardsTab';
import { PacksTab } from '@/components/daily-reveal/PacksTab';
import { GameRulesTab } from '@/components/daily-reveal/GameRulesTab';

export const Route = createFileRoute('/dashboard/daily-reveal/settings')({
  validateSearch: (search: Record<string, unknown>) => {
    return {
      tab: search.tab as 'rating' | 'cards' | 'packs' | 'game-rules' | undefined,
    };
  },
  component: DailyRevealSettingsPage,
});

function DailyRevealSettingsPage() {
  const search = Route.useSearch();
  const navigate = useNavigate();
  const activeTab = search.tab || 'rating';
  // Global settings page has no contest context — default to MLB as the
  // longest-running Daily Reveal sport. The admin selects a sport via the
  // RatingConfigTab picker; CardsTab has its own independent sport selector.
  const [sport, setSport] = useState('MLB');

  return (
    <PageContainer>
      <Breadcrumbs overrides={{ 'daily-reveal': 'Daily Reveal', settings: 'Settings' }} />

      <div className="flex items-center gap-4 mb-6">
        <BackButton />
        <h1 className="text-2xl font-semibold">Daily Reveal Settings</h1>
      </div>

      <Tabs
        value={activeTab}
        onValueChange={value =>
          navigate({
            to: '/dashboard/daily-reveal/settings',
            search: { tab: value as 'rating' | 'cards' | 'packs' | 'game-rules' },
          })
        }
        className="w-full"
      >
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="rating">Rating Config</TabsTrigger>
          <TabsTrigger value="cards">Cards</TabsTrigger>
          <TabsTrigger value="packs">Packs</TabsTrigger>
          <TabsTrigger value="game-rules">Game Rules</TabsTrigger>
        </TabsList>

        <TabsContent value="rating" className="space-y-4">
          <RatingConfigTab sport={sport} onSportChange={setSport} />
        </TabsContent>

        <TabsContent value="cards" className="space-y-4">
          <CardsTab />
        </TabsContent>

        <TabsContent value="packs" className="space-y-4">
          <PacksTab />
        </TabsContent>

        <TabsContent value="game-rules" className="space-y-4">
          <GameRulesTab />
        </TabsContent>
      </Tabs>
    </PageContainer>
  );
}
